/**
 * Reglas del service worker (sin estado, se prueban aparte): qué hace con cada petición y qué
 * respuestas del API se pueden guardar. Las usa `swCore.ts`.
 */

/** Header de la copia guardada: cuándo llegó de la red (epoch en ms). */
export const SAVED_AT_HEADER = 'X-Parche-Guardado';
/** Header de una respuesta servida desde la caché sin red: cuándo se guardó (lo lee `api/connectivity.ts`). */
export const SW_CACHE_HEADER = 'X-Parche-Cache';

/**
 * - `navigate`: una pantalla de la app: se responde con el shell precargado (la SPA resuelve la ruta).
 * - `api`: GET público del API: red primero y, sin red, la última copia.
 * - `static`: archivos con hash y estáticos: caché primero.
 * - `bypass`: el service worker no interviene (el navegador va a la red como siempre).
 */
export type RequestKind = 'navigate' | 'api' | 'static' | 'bypass';

export interface RequestLike {
  method: string;
  url: string;
  mode: string;
  headers: { has(name: string): boolean };
}

/** Rutas del mismo origen que nunca pasan por el service worker. */
const BYPASS_PATHS = [/^\/healthz$/, /^\/readyz$/, /^\/sw\.js$/, /^\/openapi\.json$/, /^\/(docs|redoc)(\/|$)/, /^\/webhooks\//];
const STATIC_FILE = /\.(?:js|mjs|css|woff2?|svg|png|jpe?g|webp|avif|ico|webmanifest|json|txt)$/i;

export function classifyRequest(request: RequestLike, origin: string): RequestKind {
  if (request.method !== 'GET') return 'bypass';
  let url: URL;
  try {
    url = new URL(request.url);
  } catch {
    return 'bypass';
  }
  if (url.origin !== origin) return 'bypass';
  const { pathname } = url;
  if (BYPASS_PATHS.some((pattern) => pattern.test(pathname))) return 'bypass';
  if (pathname.startsWith('/api/')) {
    // Con sesión (Authorization) va directo a la red y nunca se guarda: datos de una persona. La
    // autenticación tampoco se guarda.
    if (request.headers.has('authorization') || pathname.startsWith('/api/v1/auth/')) return 'bypass';
    return 'api';
  }
  if (request.mode === 'navigate') return 'navigate';
  if (pathname.startsWith('/assets/') || STATIC_FILE.test(pathname)) return 'static';
  return 'bypass';
}

export interface ResponseLike {
  status: number;
  type?: string;
  headers: { get(name: string): string | null };
}

/**
 * Una respuesta del API se guarda solo si es un 200 del mismo origen que no pide lo contrario
 * (`no-store`, `private`) y no fija cookies. Llega aquí sin Authorization (ver `classifyRequest`):
 * su `Vary: Authorization` la deja como la versión anónima, que es la única que se guarda.
 */
export function isCacheableApiResponse(response: ResponseLike): boolean {
  if (response.status !== 200) return false;
  if (response.type && response.type !== 'basic' && response.type !== 'default') return false;
  const cacheControl = (response.headers.get('cache-control') ?? '').toLowerCase();
  if (/\b(no-store|private)\b/.test(cacheControl)) return false;
  if (response.headers.get('set-cookie')) return false;
  if ((response.headers.get('vary') ?? '').includes('*')) return false;
  return true;
}

/**
 * Teselas del mapa ilustrado (`/map/v4/8192/3-5.webp`): la carpeta cambia de versión si cambia el
 * dibujo (`MAP_VERSION` en `features/map/tiles.ts`), así que tampoco cambian. No se precargan (son 340): se guardan al usarse.
 */
export function isMapTile(url: string): boolean {
  try {
    return /^\/map\/v\d+\/\d+\/\d+-\d+\.webp$/.test(new URL(url).pathname);
  } catch {
    return false;
  }
}

/** Archivos con hash de Vite (`/assets/nombre-HASH.ext`): nunca cambian, se pueden guardar para siempre. */
export function isHashedAsset(url: string): boolean {
  try {
    return /^\/assets\/.+-[\w-]{8,}\.\w+$/.test(new URL(url).pathname);
  } catch {
    return false;
  }
}
